import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { targetsApi } from "../../Components/targetsApi";
import { getProgramsList, getCoursesList } from "../marketing/marketingApi";
import { toast } from "react-toastify";
import styles from "../CreateEnquiryWizard.module.scss";

const METRICS = [
  { value: "conversion", label: "Conversions" },
  { value: "revenue", label: "Revenue" },
  { value: "conversion_rate", label: "Conversion Rate" },
];
const SCOPES = [
  { value: "overall", label: "Overall" },
  { value: "program", label: "Program" },
  { value: "course", label: "Course" },
];
const REWARD_TYPES = [
  { value: "cash", label: "Cash" },
  { value: "commission", label: "Commission" },
  { value: "bonus", label: "Bonus" },
  { value: "gift", label: "Gift" },
  { value: "custom", label: "Custom" },
];

export default function EditTargetPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    employee_ids: [],
    metric: "conversion",
    scope_type: "overall",
    scope_id: "",
    goal: "",
    start_date: "",
    end_date: "",
    reward_type: "cash",
    reward_value: "",
    reward_description: "",
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Resolved program/course name from API (so disabled scope dropdowns never show a bare id). */
  const [scopeDisplayHint, setScopeDisplayHint] = useState("");

  useEffect(() => {
    if (!id) return;
    Promise.all([
      targetsApi.getById(id),
      targetsApi.employees(),
      getProgramsList(),
    ])
      .then(([targetData, empData, progData]) => {
        const t = targetData.target;
        if (!t) {
          toast.error("Target not found");
          navigate("/targets");
          return;
        }
        setEmployees(empData.employees || []);
        setScopeDisplayHint(t.scope_display || "");
        const plist = Array.isArray(progData) ? progData : [];
        const sid = t.scope_id != null ? String(t.scope_id) : "";
        const st = t.scope_type || "overall";
        if (st === "program" && sid && !plist.some((p) => String(p.id) === sid)) {
          setPrograms([...plist, { id: Number(sid), name: t.scope_display || `Program #${sid}` }]);
        } else {
          setPrograms(plist);
        }
        setFormData({
          name: t.name || "",
          description: t.description || "",
          employee_ids: (t.employees || []).map((e) => e.id),
          metric: t.metric || "conversion",
          scope_type: t.scope_type || "overall",
          scope_id: t.scope_id != null ? String(t.scope_id) : "",
          goal: t.goal != null ? String(t.goal) : "",
          start_date: t.start_date || "",
          end_date: t.end_date || "",
          reward_type: t.reward_type || "cash",
          reward_value:
            t.reward_value != null && t.reward_value !== ""
              ? String(Math.round(Number(t.reward_value)))
              : "",
          reward_description: t.reward_description || "",
        });
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load target");
        navigate("/targets");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (formData.scope_type === "program" && formData.scope_id) {
      getCoursesList(Number(formData.scope_id))
        .then((data) => setCourses(Array.isArray(data) ? data : []))
        .catch(() => setCourses([]));
    } else if (formData.scope_type === "course") {
      const cid = String(formData.scope_id);
      getCoursesList(null)
        .then((data) => {
          const arr = Array.isArray(data) ? data : [];
          if (cid && !arr.some((c) => String(c.id) === cid)) {
            setCourses([...arr, { id: Number(cid), name: scopeDisplayHint || `Course #${cid}` }]);
          } else {
            setCourses(arr);
          }
        })
        .catch(() => setCourses([]));
    } else {
      setCourses([]);
    }
  }, [formData.scope_type, formData.scope_id, scopeDisplayHint]);

  const setFieldValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value, ...(field === "scope_type" ? { scope_id: "" } : {}) }));
    setErrors((prev) => ({ ...prev, [field]: null }));
    setApiError("");
  };

  const validate = () => {
    const nextErrors = {};
    if (!(formData.name || "").trim()) nextErrors.name = "Target name is required.";
    if (!(formData.employee_ids || []).length) nextErrors.employee_ids = "Select at least one employee.";
    if (formData.scope_type === "program" && !formData.scope_id) nextErrors.scope_id = "Select a program.";
    if (formData.scope_type === "course" && !formData.scope_id) nextErrors.scope_id = "Select a course.";
    const goalNum = Number(formData.goal);
    if (!formData.goal || isNaN(goalNum) || goalNum <= 0) nextErrors.goal = "Goal must be a positive number.";
    if (!(formData.start_date || "").trim()) nextErrors.start_date = "Start date is required.";
    if (!(formData.end_date || "").trim()) nextErrors.end_date = "End date is required.";
    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) nextErrors.end_date = "End date must be on or after start date.";
    if (formData.reward_type === "cash") {
      const rv = parseInt(String(formData.reward_value).trim(), 10);
      if (!formData.reward_value || String(formData.reward_value).trim() === "" || Number.isNaN(rv) || rv < 1) {
        nextErrors.reward_value = "Enter a whole number cash reward (e.g. 5000).";
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setApiError("");
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        employee_ids: formData.employee_ids,
        goal: Number(formData.goal),
        start_date: formData.start_date,
        end_date: formData.end_date,
        reward_type: formData.reward_type,
        reward_description: formData.reward_description.trim() || null,
      };
      if (formData.reward_type === "cash") {
        payload.reward_value = parseInt(String(formData.reward_value).trim(), 10);
      }
      await targetsApi.update(id, payload);
      toast.success("Target updated.");
      navigate(`/targets/${id}`);
    } catch (err) {
      setApiError(err?.message || "Failed to update target.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderError = (field) => errors[field] ? <div className={styles.errorText} role="alert">{errors[field]}</div> : null;

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.contentCol}>
            <div className="d-flex align-items-center justify-content-center py-5">
              <Spinner animation="border" /><span className="ms-2">Loading...</span>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.contentCol}>
          <Card className={styles.wizardCard}>
            <Card.Body className={styles.cardBody}>
              <Button type="button" variant="link" className={styles.gwBackBtn} onClick={() => navigate(`/targets/${id}`)}>
                ← Back to Target
              </Button>
              <h2 className={styles.gwTitle}>Edit Target</h2>
              {apiError && <Alert variant="danger">{apiError}</Alert>}
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Target Name *</Form.Label>
                      <Form.Control type="text" value={formData.name} onChange={(e) => setFieldValue("name", e.target.value)} isInvalid={Boolean(errors.name)} />
                      {renderError("name")}
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Description</Form.Label>
                      <Form.Control as="textarea" rows={2} value={formData.description} onChange={(e) => setFieldValue("description", e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Employee(s) *</Form.Label>
                      <Form.Select
                        multiple
                        value={formData.employee_ids.map(String)}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, (o) => Number(o.value));
                          setFormData((prev) => ({ ...prev, employee_ids: selected }));
                          setErrors((prev) => ({ ...prev, employee_ids: null }));
                        }}
                        isInvalid={Boolean(errors.employee_ids)}
                      >
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {`${emp.first_name || ""} ${emp.last_name || ""}`.trim() || emp.username || `User ${emp.id}`}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Hold Ctrl/Cmd to select multiple.</Form.Text>
                      {renderError("employee_ids")}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Metric</Form.Label>
                      <Form.Select value={formData.metric} onChange={(e) => setFieldValue("metric", e.target.value)} disabled>
                        {METRICS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Scope</Form.Label>
                      <Form.Select value={formData.scope_type} onChange={(e) => setFieldValue("scope_type", e.target.value)} disabled>
                        {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {formData.scope_type === "program" && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Program</Form.Label>
                        <Form.Select value={formData.scope_id} onChange={(e) => setFieldValue("scope_id", e.target.value)} disabled>
                          {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          {formData.scope_id && !programs.some((p) => String(p.id) === String(formData.scope_id)) && (
                            <option value={formData.scope_id}>{scopeDisplayHint || `Program #${formData.scope_id}`}</option>
                          )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                  {formData.scope_type === "course" && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Course</Form.Label>
                        <Form.Select value={formData.scope_id} onChange={(e) => setFieldValue("scope_id", e.target.value)} disabled>
                          {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          {formData.scope_id && !courses.some((c) => String(c.id) === String(formData.scope_id)) && (
                            <option value={formData.scope_id}>{scopeDisplayHint || `Course #${formData.scope_id}`}</option>
                          )}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Goal Value *</Form.Label>
                      <Form.Control
                        type="number"
                        step="any"
                        min={0}
                        value={formData.goal}
                        onChange={(e) => setFieldValue("goal", e.target.value)}
                        isInvalid={Boolean(errors.goal)}
                      />
                      {renderError("goal")}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Start Date *</Form.Label>
                      <Form.Control type="date" value={formData.start_date} onChange={(e) => setFieldValue("start_date", e.target.value)} isInvalid={Boolean(errors.start_date)} />
                      {renderError("start_date")}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>End Date *</Form.Label>
                      <Form.Control type="date" value={formData.end_date} min={formData.start_date || undefined} onChange={(e) => setFieldValue("end_date", e.target.value)} isInvalid={Boolean(errors.end_date)} />
                      {renderError("end_date")}
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Reward Type</Form.Label>
                      <Form.Select value={formData.reward_type} onChange={(e) => setFieldValue("reward_type", e.target.value)}>
                        {REWARD_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {formData.reward_type === "cash" && (
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Cash reward amount *</Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          step={1}
                          value={formData.reward_value}
                          onChange={(e) => setFieldValue("reward_value", e.target.value)}
                          isInvalid={Boolean(errors.reward_value)}
                          placeholder="Whole number (e.g. 5000)"
                        />
                        {renderError("reward_value")}
                      </Form.Group>
                    </Col>
                  )}
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>{formData.reward_type === "cash" ? "Notes (optional)" : "Reward description"}</Form.Label>
                      <Form.Control type="text" value={formData.reward_description} onChange={(e) => setFieldValue("reward_description", e.target.value)} />
                    </Form.Group>
                  </Col>
                </Row>
                <div className={styles.actions} style={{ marginTop: 24 }}>
                  <Button type="button" variant="outline-secondary" onClick={() => navigate(`/targets/${id}`)} disabled={isSubmitting}>Cancel</Button>
                  <Button type="submit" variant="primary" disabled={isSubmitting}>
                    {isSubmitting ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
      </div>
    </div>
  );
}
